import { Add, Remove, Sort } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect, memo, useRef, useMemo } from "react";

import { useDebounce } from "../../../../../../hooks/useDebounce";
import ShowMoreButton from "../../../../../global/ShowMoreButton";
import { useFetchFilterOpts } from "../../../hooks/useFetchFilterOpts";
import { MultiSelectDB } from "../../../utils/store";

import ActiveFilterIndicator from "./ActiveFilterIndicator";

const MultiSelectSearch = ({
  apiUrl,
  fieldName,
  isActive,
  nameKey,
  orderingVal,
  options: staticOptions = [],
  searchTitle,
  selectedItems,
  title,
  updateFilter,
  valueKey,
  noShowMore = false
}) => {
  const [limit, setLimit] = useState(100);
  const [checked, setChecked] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const [searchVal, setSearchVal] = useState("");
  const debouncedSearchVal = useDebounce(searchVal, 500);
  const { isLoading, options, showMoreVisible, getOptions } = useFetchFilterOpts();
  const hasStaticOptions = Array.isArray(staticOptions) && staticOptions.length > 0;
  const optionsToShow = useMemo(() => {
    if (!hasStaticOptions) {
      return options;
    }

    return staticOptions.filter((item) =>
      String(item[nameKey] || "")
        .toLowerCase()
        .includes(debouncedSearchVal.toLowerCase())
    );
  }, [hasStaticOptions, staticOptions, options, nameKey, debouncedSearchVal]);

  const initChecked = async () => {
    const storedChecked = await MultiSelectDB.get(fieldName);
    const selectedObjects = storedChecked.filter((option) =>
      selectedItems.includes(String(option[valueKey]))
    );

    if (selectedObjects.length > 0) {
      const currentCheckedValues = checked.map((item) => String(item[valueKey]));
      const newCheckedValues = selectedObjects.map((item) => String(item[valueKey]));

      const isDifferent =
        currentCheckedValues.length !== newCheckedValues.length ||
        currentCheckedValues.some((val) => !newCheckedValues.includes(val));

      if (isDifferent) {
        setChecked(selectedObjects);
      }
    }
  };

  useEffect(() => {
    if (!showForm) {
      return;
    }

    if (hasStaticOptions) {
      return;
    }
    getOptions({ apiUrl, limit, orderingVal, searchVal: debouncedSearchVal });
  }, [showForm, debouncedSearchVal, limit, apiUrl, orderingVal, hasStaticOptions]);

  useEffect(() => {
    if (!showForm || !isFocused || !inputRef.current) {
      return;
    }

    const timerId = setTimeout(() => {
      inputRef.current?.focus();
    }, 500);

    return () => clearTimeout(timerId);
  }, [showForm, isFocused, optionsToShow?.length]);

  useEffect(() => {
    if (
      selectedItems &&
      selectedItems.length > 0 &&
      optionsToShow &&
      optionsToShow.length > 0 &&
      showForm
    ) {
      initChecked();
    } else if (!selectedItems || selectedItems.length === 0) {
      /*This check ensures that intiially searchValue isn't set to empty string*/
      if (checked && checked.length > 0) {
        setChecked([]);
        setSearchVal("");
        return;
      }
      setChecked([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, optionsToShow, valueKey, showForm]);

  const handleSelectedItem = async (value) => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    const selections = newChecked.map((item) => item[valueKey]);
    updateFilter(fieldName, selections.length > 0 ? selections : null);
    await MultiSelectDB.save(fieldName, newChecked);
  };

  const truncate = (str, max) => {
    if (!str) {
      return;
    }
    return str.length > max ? str.substring(0, max) + "..." : str;
  };

  return (
    <div className="relative w-full bg-white">
      {/* Header and button to open form */}
      <div className="flex items-center">
        <Button
          onClick={() => setShowForm(!showForm)}
          className="mb-2 w-full justify-between p-0 text-left normal-case"
        >
          <div className="flex flex-row items-baseline">
            <span className="pr-2 text-black">{title}</span>
            {isActive && <ActiveFilterIndicator />}
          </div>

          {!showForm ? <Add className="text-sky-600" /> : <Remove className="text-sky-600" />}
        </Button>
      </div>

      {/* List elements */}
      {showForm && (
        <div className="px-4">
          <TextField
            className="mt-2 w-full rounded-lg text-sm placeholder:text-center"
            InputLabelProps={{ shrink: true, disabled: isLoading }}
            inputRef={inputRef}
            label={searchTitle}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => setSearchVal(event.target.value)}
            onFocus={() => setIsFocused(true)}
            placeholder={searchTitle}
            size="small"
            value={searchVal}
            disabled={isLoading}
          />
          {isLoading && !hasStaticOptions ? (
            <div className="flex place-content-center p-12">
              <CircularProgress color="primary" />
            </div>
          ) : (
            <>
              <List className="mt-4 h-[25vh] w-full items-start overflow-y-auto p-0">
                {checked?.length > 0 && (
                  <ListItem className="mb-4 block border-b border-black px-0 py-0">
                    <span className="w-full py-2 text-sm">
                      <Sort className="mr-2" /> Selected {title}
                    </span>
                    <List>
                      {checked.map((item) => {
                        const labelId = `checkbox-list-label-${item[valueKey]}`;
                        return (
                          <ListItem key={"selected_" + item[valueKey]} className="px-0 py-0">
                            <ListItemButton
                              role={undefined}
                              onClick={() => handleSelectedItem(item)}
                              className="px-0 py-0"
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ "aria-labelledby": labelId }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                id={labelId}
                                primary={
                                  <div>
                                    <Typography className="break-all">
                                      {truncate(item[nameKey], 75)}
                                    </Typography>
                                  </div>
                                }
                              ></ListItemText>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </ListItem>
                )}
                {optionsToShow?.length > 0 ? (
                  optionsToShow
                    .filter(
                      (item) =>
                        !checked.some((checkedItem) => checkedItem[valueKey] === item[valueKey])
                    )
                    .map((item, idx) => {
                      const labelId = `checkbox-list-label-${fieldName}-${idx}`;
                      return (
                        <ListItem key={`unselected-${fieldName}-${idx}`} className="px-0 py-0">
                          <ListItemButton
                            role={undefined}
                            onClick={() => handleSelectedItem(item)}
                            className="px-0 py-0"
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={false}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              id={`${labelId}-text`}
                              primary={
                                <div>
                                  <Typography className="break-all">
                                    {truncate(item[nameKey], 75)}
                                  </Typography>
                                </div>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })
                ) : (
                  <ListItem className="p-0">
                    <ListItemButton role={undefined} dense>
                      <ListItemText id={"No-Available"} primary={"No Data Available"} />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
              {!noShowMore && <ShowMoreButton
                showMoreVisible={showMoreVisible}
                setResLimit={setLimit}
                resLimit={limit}
                showMoreAmt={100}
              />
              }
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(MultiSelectSearch);
